import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Typography,
  TextField,
  MenuItem,
  InputAdornment,
  Stack,
  Tabs,
  Tab,
  Divider,
  Button,
  alpha,
  useTheme,
  TablePagination,
  Tooltip,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Avatar,
} from '@mui/material';
import {
  FilterList,
  Search,
  ArrowDownward,
  ArrowUpward,
  AttachMoney,
  AccountCircle,
  DateRange,
  Refresh,
  Receipt,
  TrendingUp,
  MonetizationOn,
  EventRepeat,
} from '@mui/icons-material';
import DetailsModal from "./DetailsModal";
import { useDonations } from '../../../hooks/useDonations';
import { formatCurrency } from '../../../utils/formatters';

interface DonationsModalProps {
  open: boolean;
  onClose: () => void;
}

// Helper function to format date
const formatDate = (dateString: string | undefined) => {
  if (!dateString) return 'Unknown date';
  
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

// Helper function to get status color
const getStatusColor = (status: string | undefined | null) => {
  if (!status) {
    return { color: 'default', text: 'Unknown' };
  }
  
  switch (status.toLowerCase()) {
    case 'succeeded':
      return { color: 'success', text: 'Succeeded' };
    case 'processing':
      return { color: 'warning', text: 'Processing' };
    case 'pending':
      return { color: 'info', text: 'Pending' };
    case 'failed':
      return { color: 'error', text: 'Failed' };
    case 'refunded':
      return { color: 'secondary', text: 'Refunded' };
    default:
      return { color: 'default', text: status };
  }
};

const DonationsModal: React.FC<DonationsModalProps> = ({ open, onClose }) => {
  const theme = useTheme();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState<{ field: string; direction: 'asc' | 'desc' }>({
    field: 'createdAt',
    direction: 'desc',
  });
  const [tabValue, setTabValue] = useState(0);

  // Get donations data with the hook
  const { donations, loading, error, refreshDonations } = useDonations({
    perPage: 200 // Get all donations
  });

  // Filter and sort donations
  const filteredDonations = useMemo(() => {
    if (!donations) return [];

    return donations
      .filter(donation => {
        // Status filter
        if (statusFilter !== 'all' && donation.status !== statusFilter) {
          return false;
        }

        // Search filter
        if (searchTerm) {
          const searchLower = searchTerm.toLowerCase();
          return (
            (donation.id && donation.id.toLowerCase().includes(searchLower)) ||
            (donation.donorId && donation.donorId.toLowerCase().includes(searchLower)) ||
            (donation.status && donation.status.toLowerCase().includes(searchLower)) ||
            (donation.amount && donation.amount.toString().includes(searchLower))
          );
        }

        return true;
      })
      .sort((a, b) => {
        if (sortBy.field === 'amount') {
          // Handle undefined amounts
          const amountA = a.amount || 0;
          const amountB = b.amount || 0;
          return sortBy.direction === 'asc'
            ? amountA - amountB
            : amountB - amountA;
        } else if (sortBy.field === 'createdAt') {
          // Handle undefined dates
          const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          return sortBy.direction === 'asc'
            ? dateA - dateB
            : dateB - dateA;
        }
        return 0;
      });
  }, [donations, searchTerm, statusFilter, sortBy]);

  // Calculate metrics for the summary cards
  const metrics = useMemo(() => {
    if (!donations) return { total: 0, avgAmount: 0, count: 0, recurring: 0 };

    const successfulDonations = donations.filter(d => d.status === 'succeeded');
    const total = successfulDonations.reduce((sum, d) => sum + (d.amount || 0), 0);
    const count = successfulDonations.length;
    const avgAmount = count > 0 ? total / count : 0;
    const recurring = successfulDonations.filter(d => d.frequency === 'recurring').length;

    return { total, avgAmount, count, recurring };
  }, [donations]);

  // Handle sorting
  const handleSort = (field: string) => {
    setSortBy(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  // Handle pagination
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle tab change
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setStatusFilter(
      newValue === 0 ? 'all' :
      newValue === 1 ? 'succeeded' :
      newValue === 2 ? 'processing' :
      newValue === 3 ? 'failed' : 'all'
    );
  };

  // Reset filters
  const handleReset = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setTabValue(0);
    setSortBy({ field: 'createdAt', direction: 'desc' });
  };

  return (
    <DetailsModal 
      open={open} 
      onClose={onClose} 
      title="Donation Management"
      maxWidth="lg"
    >
      {/* Metrics Summary Section */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            height: '100%',
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            borderLeft: `4px solid ${theme.palette.primary.main}`,
            transition: 'transform 0.3s',
            '&:hover': { transform: 'translateY(-4px)' }
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Typography variant="subtitle2" color="textSecondary">TOTAL DONATIONS</Typography>
                <Avatar 
                  sx={{ 
                    bgcolor: theme.palette.primary.main,
                    width: 36,
                    height: 36
                  }}
                >
                  <MonetizationOn />
                </Avatar>
              </Box>
              <Typography variant="h4" sx={{ mt: 2, fontWeight: 'bold' }}>
                {formatCurrency(metrics.total)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            height: '100%',
            bgcolor: alpha(theme.palette.success.main, 0.1),
            borderLeft: `4px solid ${theme.palette.success.main}`,
            transition: 'transform 0.3s',
            '&:hover': { transform: 'translateY(-4px)' }
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Typography variant="subtitle2" color="textSecondary">TOTAL COUNT</Typography>
                <Avatar 
                  sx={{ 
                    bgcolor: theme.palette.success.main,
                    width: 36,
                    height: 36
                  }}
                >
                  <Receipt />
                </Avatar>
              </Box>
              <Typography variant="h4" sx={{ mt: 2, fontWeight: 'bold' }}>
                {metrics.count}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            height: '100%',
            bgcolor: alpha(theme.palette.info.main, 0.1),
            borderLeft: `4px solid ${theme.palette.info.main}`,
            transition: 'transform 0.3s',
            '&:hover': { transform: 'translateY(-4px)' }
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Typography variant="subtitle2" color="textSecondary">AVG DONATION</Typography>
                <Avatar 
                  sx={{ 
                    bgcolor: theme.palette.info.main,
                    width: 36,
                    height: 36
                  }}
                >
                  <TrendingUp />
                </Avatar>
              </Box>
              <Typography variant="h4" sx={{ mt: 2, fontWeight: 'bold' }}>
                {formatCurrency(metrics.avgAmount)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            height: '100%',
            bgcolor: alpha(theme.palette.secondary.main,.1),
            borderLeft: `4px solid ${theme.palette.secondary.main}`,
            transition: 'transform 0.3s',
            '&:hover': { transform: 'translateY(-4px)' }
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Typography variant="subtitle2" color="textSecondary">RECURRING</Typography>
                <Avatar 
                  sx={{ 
                    bgcolor: theme.palette.secondary.main,
                    width: 36,
                    height: 36
                  }}
                >
                  <EventRepeat />
                </Avatar>
              </Box>
              <Typography variant="h4" sx={{ mt: 2, fontWeight: 'bold' }}>
                {metrics.recurring}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs & Filters Section */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="All Donations" />
            <Tab label="Successful" />
            <Tab label="Processing" />
            <Tab label="Failed" />
          </Tabs>
        </Box>
        
        <Stack 
          direction={{ xs: 'column', sm: 'row' }} 
          spacing={2} 
          sx={{ mt: 2 }}
          alignItems="center"
          justifyContent="space-between"
        >
          <TextField
            placeholder="Search donations..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            variant="outlined"
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ width: { xs: '100%', sm: '300px' } }}
          />
          
          <Stack direction="row" spacing={1}>
            <Button 
              size="small" 
              variant="outlined" 
              color="primary" 
              startIcon={<Refresh />}
              onClick={refreshDonations}
            >
              Refresh
            </Button>
            <Button 
              size="small" 
              variant="outlined" 
              color="secondary" 
              startIcon={<FilterList />}
              onClick={handleReset}
            >
              Reset Filters
            </Button>
          </Stack>
        </Stack>
      </Box>

      {/* Data Table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="error">
            Error loading donations. Please try again.
          </Typography>
        </Box>
      ) : (
        <>
          <TableContainer 
            component={Paper} 
            elevation={0}
            sx={{ 
              mb: 2,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
              maxHeight: 'calc(100vh - 350px)',
              minHeight: '300px'
            }}
          >
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 'bold', bgcolor: alpha(theme.palette.primary.main, 0.1) } }}>
                  <TableCell>
                    <Box 
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        cursor: 'pointer',
                        userSelect: 'none'
                      }}
                      onClick={() => handleSort('id')}
                    >
                      ID
                      {sortBy.field === 'id' && (
                        sortBy.direction === 'asc' 
                          ? <ArrowUpward fontSize="small" sx={{ ml: 0.5 }} /> 
                          : <ArrowDownward fontSize="small" sx={{ ml: 0.5 }} />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box 
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        cursor: 'pointer',
                        userSelect: 'none'
                      }}
                      onClick={() => handleSort('donorId')}
                    >
                      Donor ID
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box 
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        cursor: 'pointer',
                        userSelect: 'none'
                      }}
                      onClick={() => handleSort('amount')}
                    >
                      Amount
                      {sortBy.field === 'amount' && (
                        sortBy.direction === 'asc' 
                          ? <ArrowUpward fontSize="small" sx={{ ml: 0.5 }} /> 
                          : <ArrowDownward fontSize="small" sx={{ ml: 0.5 }} />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>
                    <Box 
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        cursor: 'pointer',
                        userSelect: 'none'
                      }}
                      onClick={() => handleSort('createdAt')}
                    >
                      Date
                      {sortBy.field === 'createdAt' && (
                        sortBy.direction === 'asc' 
                          ? <ArrowUpward fontSize="small" sx={{ ml: 0.5 }} /> 
                          : <ArrowDownward fontSize="small" sx={{ ml: 0.5 }} />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>Frequency</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredDonations.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                      <Typography variant="subtitle1" color="textSecondary">
                        No donations found
                      </Typography>
                      <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                        Try changing your search criteria or filters
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredDonations.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((donation) => {
                    const status = getStatusColor(donation.status);
                    
                    return (
                      <TableRow 
                        key={donation.id}
                        hover
                        sx={{
                          '&:hover': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.05),
                          }
                        }}
                      >
                        <TableCell width="22%">
                          <Tooltip title={donation.id || ''}>
                            <Typography variant="body2" noWrap sx={{ maxWidth: 120 }}>
                              {donation.id ? `${donation.id.substring(0, 16)}...` : 'Unknown ID'}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell width="22%">
                          <Tooltip title={donation.donorId || ''}>
                            <Typography variant="body2" noWrap sx={{ maxWidth: 120 }}>
                              {donation.donorId ? `${donation.donorId.substring(0, 16)}...` : 'Unknown Donor'}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell width="15%">
                          <Typography variant="body2" fontWeight="bold">
                            {formatCurrency(donation.amount)}
                          </Typography>
                        </TableCell>
                        <TableCell width="15%">
                          <Chip
                            size="small"
                            label={status.text}
                            color={status.color as any}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell width="18%">
                          {formatDate(donation.createdAt)}
                        </TableCell>
                        <TableCell width="8%">
                          {donation.frequency === 'recurring' ? (
                            <Chip
                              size="small"
                              label="Recurring"
                              color="secondary"
                              variant="outlined"
                            />
                          ) : (
                            <Chip
                              size="small"
                              label="One-time"
                              color="default"
                              variant="outlined"
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={filteredDonations.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </>
      )}
    </DetailsModal>
  );
};

export default DonationsModal;
